"use client";

import { useState } from "react";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { safeRedirectPath } from "@/lib/auth/safe-redirect";
import { buildResetRedirectUrl } from "@/app/auth/reset-password/lib/request-reset";

interface AuthFormProps {
  redirectTo?: string;
  /** ?error= code from /auth/callback or /auth/confirm when a link fails. */
  initialError?: string;
}

// Turn the ?error= codes that /auth/callback and /auth/confirm redirect to on a
// failed link (expired, already used, missing/invalid token) into friendly,
// actionable copy. Without this the user lands on a blank form with no reason.
function friendlyAuthError(code: string | undefined): string | null {
  if (!code) return null;
  switch (code) {
    case "missing_token":
      return "That link is missing its security token. Request a fresh one below and we'll email it over.";
    case "invalid_type":
      return "That sign-in link wasn't recognised. Request a fresh one below.";
    case "callback_failed":
      return "We couldn't complete that link — it may have expired or already been used. Request a fresh one below and we'll send a new one.";
    default:
      // Raw Supabase messages (e.g. "Email link is invalid or has expired").
      return "That link has expired or has already been used. Request a fresh one below and we'll email you a new link.";
  }
}

export function AuthForm({ redirectTo, initialError }: AuthFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [mode, setMode] = useState<"signin" | "signup" | "forgot">("signin");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(
    friendlyAuthError(initialError),
  );
  const [message, setMessage] = useState<string | null>(null);
  /**
   * Consecutive sign-in failures. After five identical failures a real user
   * was left with the same one-line error and no next step (mystery shopper,
   * 2026-08-27) — the moment someone is most likely to give up is exactly
   * when the interface goes quiet. Reset on any success or mode change.
   */
  const [failedAttempts, setFailedAttempts] = useState(0);

  /** Switching intent (sign in / sign up / forgot) starts the count over. */
  const switchMode = (next: "signin" | "signup" | "forgot") => {
    setMode(next);
    setFailedAttempts(0);
    setError(null);
  };

  const supabase = createClient();

  async function handleEmailAuth(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    if (mode === "forgot") {
      const trimmedEmail = email.trim().toLowerCase();
      if (!trimmedEmail) {
        setError("Enter your email");
        setLoading(false);
        return;
      }
      // buildResetRedirectUrl is the canonical reset-redirect builder —
      // shared with RequestNewLinkForm to prevent drift.
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        trimmedEmail,
        {
          redirectTo: buildResetRedirectUrl(window.location.origin),
        },
      );
      if (resetError) {
        setError(resetError.message);
      } else {
        setMessage(
          `If an account exists for ${trimmedEmail}, a reset link is on its way. Check your inbox (and spam) — the email comes from recover@framis.app.`,
        );
      }
      setLoading(false);
      return;
    }

    if (mode === "signin") {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (authError) {
        setError(authError.message);
        setFailedAttempts((n) => n + 1);
      } else {
        setFailedAttempts(0);
        // Role-based landing — admin → /admin, else photographer.
        // safeRedirectPath guards against open-redirect via ?redirectTo=https://evil.com
        const safeRedirect = safeRedirectPath(redirectTo, "");
        let landing = safeRedirect || "/photographer/images";
        if (!safeRedirect) {
          try {
            const {
              data: { user: signedIn },
            } = await supabase.auth.getUser();
            if (signedIn) {
              const { data: profile } = await supabase
                .from("users")
                .select("role")
                .eq("id", signedIn.id)
                .maybeSingle();
              if (profile?.role === "admin") landing = "/admin";
            }
          } catch {
            // Default to photographer.
          }
        }
        window.location.href = landing;
        return;
      }
    } else {
      const trimmedName = displayName.trim();
      if (trimmedName.length < 2) {
        setError("Please enter your name or studio name (at least 2 characters).");
        setLoading(false);
        return;
      }
      const { error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            display_name: trimmedName,
          },
        },
      });
      if (authError) {
        setError(authError.message);
      } else {
        // Drop a CRM lead row immediately so the photographer is in the
        // pipeline even before they confirm their email. Idempotent on email.
        try {
          await fetch("/api/leads", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email,
              displayName: trimmedName,
              source: "website",
              notes: "Signed up via /auth — pending email confirmation",
            }),
          });
        } catch {
          // best-effort; don't surface CRM errors to the photographer
        }
        setMessage(
          `Thanks ${trimmedName.split(" ")[0]} — check your inbox for the confirmation email. Click the link inside and you'll land back on framis.app ready to upload your library.`,
        );
      }
    }

    setLoading(false);
  }

  return (
    <div
      className="rounded-[2px] backdrop-blur-sm p-6"
      style={{
        background: "rgba(247,240,230,0.04)",
        border: "1px solid rgba(247,240,230,0.12)",
      }}
    >
      <form onSubmit={handleEmailAuth} className="space-y-4" noValidate>
        {mode === "signup" && (
          <div className="space-y-1.5">
            <Label
              htmlFor="display-name"
              className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground"
            >
              Your name or studio
            </Label>
            <Input
              id="display-name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Maria Costa Photography"
              required
              autoComplete="name"
              className="h-10 bg-background/60"
            />
          </div>
        )}
        <div className="space-y-1.5">
          <Label
            htmlFor="email"
            className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground"
          >
            Email
          </Label>
          <Input
            id="email"
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@studio.com"
            required
            autoComplete="email"
            spellCheck={false}
            className="h-10 bg-background/60"
          />
        </div>
        {mode !== "forgot" && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label
                htmlFor="password"
                className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground"
              >
                Password
              </Label>
              {mode === "signin" && (
                <button
                  type="button"
                  onClick={() => {
                    setMode("forgot");
                    setError(null);
                    setMessage(null);
                  }}
                  className="font-mono text-[10px] tracking-[0.18em] uppercase text-muted-foreground hover:text-accent transition-colors"
                >
                  Forgot?
                </button>
              )}
            </div>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === "signup" ? "At least 8 characters…" : ""}
              required
              minLength={mode === "signup" ? 8 : undefined}
              autoComplete={
                mode === "signin" ? "current-password" : "new-password"
              }
              className="h-10 bg-background/60"
            />
          </div>
        )}

        <div aria-live="polite" className="min-h-0">
          {error && (
            <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 px-3 py-2 rounded-md break-words">
              {error}
            </p>
          )}
          {/* Escalating help. Repeating the same one-line error indefinitely
              leaves someone stuck with no next step — the shopper hit five
              identical failures and was given nothing further to try. Two
              tiers: a nudge toward password reset, then a human to contact. */}
          {mode === "signin" && failedAttempts >= 2 && (
            <p
              role="status"
              className="text-sm text-muted-foreground bg-muted/40 border border-border px-3 py-2 rounded-md break-words"
            >
              {failedAttempts >= 4 ? (
                <>
                  Still not working? That is on us to help with — email{" "}
                  <a
                    href="mailto:support@framis.app?subject=Cannot%20sign%20in"
                    className="underline underline-offset-4"
                  >
                    support@framis.app
                  </a>{" "}
                  and we will get you in. Include the address you are trying.
                </>
              ) : (
                <>
                  Two attempts have failed. If you are unsure of your password,{" "}
                  <button
                    type="button"
                    onClick={() => switchMode("forgot")}
                    className="underline underline-offset-4"
                  >
                    reset it
                  </button>{" "}
                  rather than keep guessing — repeated attempts can lock the
                  account for a while.
                </>
              )}
            </p>
          )}
          {message && (
            <p className="text-sm text-accent bg-accent/10 border border-accent/30 px-3 py-2 rounded-md break-words">
              {message}
            </p>
          )}
        </div>

        <Button type="submit" className="w-full h-10 font-medium" disabled={loading}>
          {loading
            ? "Please wait…"
            : mode === "signin"
              ? "Sign in"
              : mode === "signup"
                ? "Create account"
                : "Send reset link"}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        {mode === "signin" && (
          <>
            No account?{" "}
            <button
              type="button"
              onClick={() => {
                setMode("signup");
                setError(null);
                setMessage(null);
              }}
              className="text-foreground underline underline-offset-4 hover:text-accent transition-colors"
            >
              Create one
            </button>
          </>
        )}
        {mode === "signup" && (
          <>
            Already registered?{" "}
            <button
              type="button"
              onClick={() => {
                setMode("signin");
                setError(null);
                setMessage(null);
              }}
              className="text-foreground underline underline-offset-4 hover:text-accent transition-colors"
            >
              Sign in
            </button>
          </>
        )}
        {mode === "forgot" && (
          <>
            Remembered it?{" "}
            <button
              type="button"
              onClick={() => {
                setMode("signin");
                setError(null);
                setMessage(null);
              }}
              className="text-foreground underline underline-offset-4 hover:text-accent transition-colors"
            >
              Back to sign in
            </button>
          </>
        )}
      </p>
    </div>
  );
}
